/**
 * List state engine — filter, search, sort.
 *
 * Pure state. No DOM manipulation, no animation, no timers.
 * Computes which items pass, diffs against previous state, and calls
 * onChange with entered/exited arrays. Modules own all animation.
 *
 * Sets a status attribute on elements ("active" | "not-active") as a CSS
 * hook for Webflow — but animation is driven by code, not CSS.
 *
 * @example
 * ```ts
 * import { createList } from "@lib/list";
 *
 * const list = createList(itemElements, {
 *   onChange: (result) => {
 *     // gsap animate result.entered in / result.exited out
 *   },
 * });
 *
 * list.toggleFilter("cat", "crms");
 * list.search("salesforce");
 * list.addItems(newElements);
 * ```
 */

// -------------------------------------------------------
// Types
// -------------------------------------------------------

export interface ListItem {
  el: HTMLElement;
  data: Record<string, string>;
  text: string;
  passesFilter: boolean;
  passesSearch: boolean;
  active: boolean;
}

export interface ListResult {
  /** Items that pass all gates, in sort order */
  active: HTMLElement[];
  /** Items that don't pass */
  inactive: HTMLElement[];
  /** Items that just became active (weren't before) */
  entered: HTMLElement[];
  /** Items that just became inactive (were active before) */
  exited: HTMLElement[];
  /** Total item count */
  total: number;
  /** Active item count */
  passing: number;
}

export interface ListConfig {
  /** "single" = radio per group, "multi" = multi-select. Default "multi". */
  filterMode?: "single" | "multi";
  /** Attribute name set on items. Default "data-list-status". */
  statusAttr?: string;
  /**
   * Applied to both filter values and item values before comparison.
   * Essential for Webflow CMS data: slugs collide across collections and gain
   * a `-XXXXX` suffix (`crms-db5ec`, `webhook-03e5c`) while the names stay
   * clean, so raw slug equality silently fails. See `slugKey` below.
   */
  matchNormalize?: (value: string) => string;
  /**
   * Fields allowed to match by fuzzy subsequence, for typo tolerance.
   * Everything else is substring-only.
   *
   * Keep this to short fields — a title, a name. Subsequence matching on a
   * paragraph matches almost anything: the letters of "millionverifier"
   * appear in order inside plenty of unrelated product descriptions.
   */
  fuzzyFields?: string[];
  /** Minimum query length before fuzzy matching kicks in. Default 3. */
  fuzzyMinLength?: number;
  /**
   * When fuzzy matching runs. Default "always".
   *
   * "fallback" only reaches for it when the substring pass found nothing,
   * which is what you want on a list of short titles: a five-letter query
   * like "email" has its letters scattered in order through plenty of
   * unrelated titles ("Customer Sentiment Details"), so always-on fuzzy
   * buries the exact hits in noise. As a fallback it still catches the typo
   * it exists for, and only then.
   */
  fuzzyMode?: "always" | "fallback";
  /**
   * Extra fields derived from the element itself, merged over its `data-*`.
   *
   * Lets a module search or filter on text it reads out of the DOM — a
   * heading, a description cell — without writing that text back into the
   * markup as a fat `data-` attribute on every one of several hundred items.
   * Runs once per item, at build time.
   */
  derive?: (el: HTMLElement) => Record<string, string>;
  /** Called after every state change */
  onChange?: (result: ListResult) => void;
}

export interface ListInstance {
  toggleFilter(field: string, value: string): void;
  setFilter(field: string, values: string[]): void;
  clearFilter(field: string): void;
  clearAllFilters(): void;
  hasFilter(field: string, value: string): boolean;

  search(query: string, fields?: string[]): void;

  sort(
    field: string,
    order?: "asc" | "desc",
    type?: "string" | "number" | "date",
  ): void;
  clearSort(): void;

  addItems(elements: HTMLElement[]): void;

  reset(): void;
  refresh(): void;

  readonly items: ListItem[];
  readonly sorted: ListItem[];
  readonly activeFilters: ReadonlyMap<string, ReadonlySet<string>>;
  readonly filterCount: number;
  readonly searchQuery: string;
  readonly searchFields: string[] | null;

  getResult(): ListResult;
  toParams(): URLSearchParams;
  fromParams(params: URLSearchParams, silent?: boolean): void;
}

// -------------------------------------------------------
// Implementation
// -------------------------------------------------------

export function createList(
  elements: HTMLElement[],
  config: ListConfig = {},
): ListInstance {
  const {
    filterMode = "multi",
    statusAttr = "data-list-status",
    matchNormalize,
    fuzzyFields,
    fuzzyMinLength = 3,
    fuzzyMode = "always",
    derive,
    onChange,
  } = config;

  const fuzzySet = fuzzyFields ? new Set(fuzzyFields) : null;
  // Flipped per pass by compute() when fuzzyMode is "fallback".
  let fuzzyEnabled = true;

  const normalize = matchNormalize ?? ((s: string) => s);

  const build = (el: HTMLElement): ListItem => {
    const item = buildItem(el);
    if (derive) Object.assign(item.data, derive(el));
    return item;
  };

  const items: ListItem[] = elements.map(build);
  let sorted: ListItem[] = [...items];
  let searchQuery = "";
  let searchFields: string[] | null = null;
  const activeFilters = new Map<string, Set<string>>();
  let currentSort: {
    field: string;
    order: "asc" | "desc";
    type: "string" | "number" | "date";
  } | null = null;

  let prevActive = new Set<HTMLElement>();

  // ---- collect ----
  // Webflow can't emit a multi-reference field as one attribute, so each
  // referenced value is rendered as its own `[data-list-collect]` element
  // (a nested Collection List). Fold those back into a comma-joined value
  // on the item so filtering reads a single field.
  function runCollect(itemList: ListItem[]) {
    itemList.forEach((item) => {
      const collectors = item.el.querySelectorAll<HTMLElement>(
        "[data-list-collect]",
      );
      if (!collectors.length) return;

      const seen = new Set<string>();
      collectors.forEach((c) => {
        const field = c.dataset.listCollect || "";
        const value = (c.dataset.listCollectValue || c.textContent || "")
          .trim()
          .toLowerCase();
        if (!field || !value || seen.has(`${field}:${value}`)) return;
        seen.add(`${field}:${value}`);
        const existing = item.data[field];
        item.data[field] = existing ? `${existing},${value}` : value;
      });
    });
  }

  runCollect(items);

  // ---- filter ----
  // AND across groups, OR within a group — the conventional facet behaviour.

  function checkFilter(item: ListItem): boolean {
    if (activeFilters.size === 0) return true;

    for (const [field, values] of activeFilters) {
      const raw = item.data[field] || "";
      const itemValues = raw
        .split(",")
        .map((v) => normalize(v.trim().toLowerCase()));
      const match = [...values].some((v) => itemValues.includes(normalize(v)));
      if (!match) return false;
    }
    return true;
  }

  function toggleFilter(field: string, value: string) {
    const lower = value.toLowerCase();

    if (filterMode === "single") {
      const group = activeFilters.get(field);
      if (group?.has(lower)) {
        activeFilters.delete(field);
      } else {
        activeFilters.set(field, new Set([lower]));
      }
    } else {
      let group = activeFilters.get(field);
      if (!group) {
        group = new Set();
        activeFilters.set(field, group);
      }
      if (group.has(lower)) {
        group.delete(lower);
        if (group.size === 0) activeFilters.delete(field);
      } else {
        group.add(lower);
      }
    }

    compute();
  }

  function setFilter(field: string, values: string[]) {
    if (values.length === 0) {
      activeFilters.delete(field);
    } else {
      activeFilters.set(field, new Set(values.map((v) => v.toLowerCase())));
    }
    compute();
  }

  function clearFilter(field: string) {
    activeFilters.delete(field);
    compute();
  }

  function clearAllFilters() {
    activeFilters.clear();
    compute();
  }

  function hasFilter(field: string, value: string) {
    return !!activeFilters.get(field)?.has(value.toLowerCase());
  }

  // ---- search ----

  function checkSearch(item: ListItem): boolean {
    if (!searchQuery) return true;

    // No explicit fields → match the item's whole text, substring only.
    // Fuzzy over a whole card's text content would match nearly everything.
    if (!searchFields) {
      return fuzzySet && fuzzyEnabled
        ? matchField(item.text, true)
        : item.text.includes(searchQuery);
    }

    // Substring pass first — a direct hit in any field wins outright.
    for (const f of searchFields) {
      if ((item.data[f] || "").toLowerCase().includes(searchQuery)) return true;
    }

    // Then typo tolerance, but only on the fields opted into it.
    if (!fuzzySet || !fuzzyEnabled || searchQuery.length < fuzzyMinLength) {
      return false;
    }
    for (const f of searchFields) {
      if (!fuzzySet.has(f)) continue;
      if (fuzzyMatch((item.data[f] || "").toLowerCase(), searchQuery)) {
        return true;
      }
    }
    return false;
  }

  function matchField(text: string, allowFuzzy: boolean): boolean {
    if (text.includes(searchQuery)) return true;
    return allowFuzzy && searchQuery.length >= fuzzyMinLength
      ? fuzzyMatch(text, searchQuery)
      : false;
  }

  function search(query: string, fields?: string[]) {
    searchQuery = query.toLowerCase().trim();
    searchFields = fields || null;
    compute();
  }

  // ---- sort ----

  function applySortOrder() {
    if (!currentSort) {
      sorted = [...items];
      return;
    }

    const { field, order, type } = currentSort;
    sorted = [...items].sort((a, b) => {
      const aVal = a.data[field] || "";
      const bVal = b.data[field] || "";

      let diff: number;
      if (type === "number") {
        diff = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
      } else if (type === "date") {
        diff = new Date(aVal).getTime() - new Date(bVal).getTime();
      } else {
        diff = aVal.localeCompare(bVal);
      }
      return order === "asc" ? diff : -diff;
    });
  }

  function sort(
    field: string,
    order: "asc" | "desc" = "desc",
    type: "string" | "number" | "date" = "string",
  ) {
    currentSort = { field, order, type };
    applySortOrder();
    compute();
  }

  function clearSort() {
    currentSort = null;
    sorted = [...items];
    compute();
  }

  // ---- add items ----
  // Called as extra CMS pages stream in. Preserves DOM order of the
  // incoming batch and keeps any active sort applied.

  function addItems(elements: HTMLElement[]) {
    const existing = new Set(items.map((i) => i.el));
    const unique = elements.filter((el) => !existing.has(el));
    if (!unique.length) return;

    const newItems = unique.map(build);
    runCollect(newItems);
    items.push(...newItems);
    applySortOrder();
    compute();
  }

  // ---- core compute ----

  /** One pass over every item. Returns how many came through. */
  function evaluate(fuzzy: boolean): number {
    fuzzyEnabled = fuzzy;
    let passing = 0;
    for (const item of items) {
      item.passesFilter = checkFilter(item);
      item.passesSearch = checkSearch(item);
      item.active = item.passesFilter && item.passesSearch;
      if (item.active) passing++;
    }
    return passing;
  }

  function markStatus() {
    for (const item of items) {
      item.el.setAttribute(statusAttr, item.active ? "active" : "not-active");
    }
  }

  /** Exact first; typo tolerance only rescues an empty result set. */
  function run() {
    const strict = fuzzyMode === "fallback";
    const passing = evaluate(!strict);
    if (strict && passing === 0 && searchQuery) evaluate(true);
  }

  function compute() {
    run();
    markStatus();

    const result = buildResult();
    prevActive = new Set(result.active);
    onChange?.(result);
  }

  function buildResult(): ListResult {
    const active: HTMLElement[] = [];
    const inactive: HTMLElement[] = [];
    for (const i of sorted) (i.active ? active : inactive).push(i.el);

    const activeSet = new Set(active);
    const entered = active.filter((el) => !prevActive.has(el));
    const exited = [...prevActive].filter((el) => !activeSet.has(el));

    return {
      active,
      inactive,
      entered,
      exited,
      total: items.length,
      passing: active.length,
    };
  }

  // ---- URL state ----

  function toParams(): URLSearchParams {
    const params = new URLSearchParams();
    for (const [field, values] of activeFilters) {
      params.set(field, [...values].join(","));
    }
    if (searchQuery) params.set("q", searchQuery);
    return params;
  }

  function fromParams(params: URLSearchParams, silent = false) {
    activeFilters.clear();
    searchQuery = "";

    params.forEach((value, key) => {
      if (key === "q") {
        searchQuery = value.toLowerCase().trim();
      } else {
        const values = value
          .split(",")
          .map((v) => v.trim().toLowerCase())
          .filter(Boolean);
        if (values.length) activeFilters.set(key, new Set(values));
      }
    });

    if (silent) applyInitial();
    else compute();
  }

  // ---- reset ----

  function reset() {
    activeFilters.clear();
    searchQuery = "";
    searchFields = null;
    currentSort = null;
    sorted = [...items];
    compute();
  }

  function refresh() {
    compute();
  }

  // ---- initial state (silent — no onChange) ----

  function applyInitial() {
    run();
    markStatus();
    prevActive = new Set(items.filter((i) => i.active).map((i) => i.el));
  }

  applyInitial();

  return {
    toggleFilter,
    setFilter,
    clearFilter,
    clearAllFilters,
    hasFilter,
    search,
    sort,
    clearSort,
    addItems,
    reset,
    refresh,

    get items() {
      return items;
    },
    get sorted() {
      return sorted;
    },
    get activeFilters() {
      return activeFilters as ReadonlyMap<string, ReadonlySet<string>>;
    },
    get filterCount() {
      let n = 0;
      for (const values of activeFilters.values()) n += values.size;
      return n;
    },
    get searchQuery() {
      return searchQuery;
    },
    get searchFields() {
      return searchFields;
    },

    getResult: () => buildResult(),
    toParams,
    fromParams,
  };
}

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------

function buildItem(el: HTMLElement): ListItem {
  return {
    el,
    data: datasetToRecord(el.dataset),
    text: (el.textContent || "").toLowerCase(),
    passesFilter: true,
    passesSearch: true,
    active: true,
  };
}

function datasetToRecord(dataset: DOMStringMap): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key in dataset) out[key] = dataset[key] || "";
  return out;
}

/**
 * Strips Webflow's collision suffix from a CMS slug.
 *
 * When two collections both contain "CRMs", Webflow keeps the first slug
 * clean and appends a 5-char hex tag to the rest — `crms` vs `crms-db5ec`,
 * `webhook` vs `webhook-03e5c`. A filter option and a card can therefore
 * carry different slugs for the same concept. Dropping a trailing
 * `-[0-9a-f]{4,6}` segment makes both sides comparable.
 *
 * Pass as `matchNormalize` when filter values come from Webflow slugs.
 */
export function slugKey(value: string): string {
  return value.trim().toLowerCase().replace(/-[0-9a-f]{4,6}$/, "");
}

/**
 * Fuzzy match — true if every character of `query` appears in `text` in
 * order, allowing gaps. Exact substring is checked first (faster, and the
 * common case).
 *
 * "salesfrce" matches "salesforce"
 * "xyz"       does NOT match "hubspot"
 */
function fuzzyMatch(text: string, query: string): boolean {
  if (text.includes(query)) return true;

  let ti = 0;
  for (let qi = 0; qi < query.length; qi++) {
    const found = text.indexOf(query[qi], ti);
    if (found === -1) return false;
    ti = found + 1;
  }
  return true;
}
